// PRD 16 LR12 (Q15, LR10 verdict P-A + D-B) — a child's passport in Tenis10 Claims and on an account's page: the badge, the
// line (the window, where it came from, when) and the day support may activate it from. The API answers the state and the
// source (`supportPassport`); Payments, for a purchase, the card or the wallet and the day (`getPassportPayments`).
import { useEffect, useState } from "react"
import moment from "moment-timezone"
import { getPassportPayments } from "./api"
import { TIMEZONE, formatDate } from "./claimsLabels"

// The programme's today and year are Romanian (the API's `tenis10ProgramYear`)
export const programToday = (now = new Date()) => moment(now).tz(TIMEZONE).format("YYYY-MM-DD")
export const programYear = (now = new Date()) => Number(programToday(now).slice(0, 4))

// a passport day is stored at UTC midnight: read it as that calendar day
const passportDay = value => moment.utc(value)

/** „1 Sep – 31 Dec 2026” (one year) or „1 Sep 2026 – 31 Dec 2027”. */
export const passportWindowText = (startDate, endDate) => {
  const start = passportDay(startDate)
  const end = passportDay(endDate)
  return start.year() === end.year()
    ? `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`
    : `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`
}

/**
 * The day support may activate a passport from (MIRROR of the API's `common/passportLifecycle` `supportPassportWindow`):
 * today or an earlier day of this year, or 1 January of next year — to 31 December of that year. → `{ year, startDate,
 * endDate }` (`YYYY-MM-DD`) or null.
 */
export const supportPassportDay = (startDay, now = new Date()) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof startDay === "string" ? startDay : "")
  if (!match || !moment.utc(startDay, "YYYY-MM-DD", true).isValid()) return null
  const today = programToday(now)
  const thisYear = Number(today.slice(0, 4))
  const year = Number(match[1])
  const allowed = (year === thisYear && startDay <= today) || (year === thisYear + 1 && match[2] === "01" && match[3] === "01")
  return allowed ? { year, startDate: startDay, endDate: `${year}-12-31` } : null
}

/** The passport's state as a badge: [tone, text]. */
export const passportBadge = passport => {
  if (passport?.state === "active") return ["success", `Active · ${passport.year}`]
  if (passport?.state === "expiring") return ["warning", "Expiring"]
  if (passport?.state === "scheduled") return ["info", `From ${passportDay(passport.startDate).format("D MMM YYYY")}`]
  return ["secondary", "Inactive"]
}

const METHOD = { card: "card", wallet: "wallet" }

/** Where the passport came from, and when (support's activation, a payment's day), in the admin's words. */
const sourceText = (passport, payment) => {
  switch (passport.source) {
    case "support":
      return [`activated by support (${passport.activatedBy?.name || `#${passport.activatedBy?.userId}`})`, passport.activatedAt && formatDate(passport.activatedAt)]
    // P45: worded as SportyaOS words it since 23.10 („Înscriere la fața locului”: nothing fronted)
    case "override":
      return ["with an entry paid on site"]
    case "walletDebitEntry":
      return [`from the parent's wallet at the club's entry${payment?.clubName ? ` (${payment.clubName})` : ""}`, payment?.paidAt && formatDate(payment.paidAt)]
    case "migration":
      return ["carried over from tenis10.ro"]
    default:
      return [`bought by the parent${METHOD[payment?.method] ? ` (${METHOD[payment.method]})` : ""}`, payment?.paidAt && formatDate(payment.paidAt)]
  }
}

/** The muted line under the badge: „1 Jan – 31 Dec 2026 · bought by the parent (card) · 3 Feb 2026”, or „No passport for 2026.” */
export const passportLine = (passport, payment, now = new Date()) => {
  if (!passport?.paid || passport.state === "inactive") return `No passport for ${programYear(now)}.`
  return [passportWindowText(passport.startDate, passport.endDate), ...sourceText(passport, payment)].filter(Boolean).join(" · ")
}

// a purchase's card or wallet and day are Payments' (an organizer's wallet entry names its club there)
const PAID_SOURCES = ["purchase", "walletDebitEntry"]

/**
 * How and when the rows' bought passports were paid: profileId → `{ method, paidAt, clubName }`. Best effort: a failed read
 * leaves the line without them („bought by the parent”).
 */
export function usePassportPayments(rows) {
  const key = (rows || []).filter(row => row.passport?.paid && PAID_SOURCES.includes(row.passport.source)).map(row => row.profileId).join(",")
  const [payments, setPayments] = useState({})
  useEffect(() => {
    setPayments({})
    if (!key) return undefined
    let current = true
    Promise.resolve().then(() => getPassportPayments(key.split(",")))
      .then(data => {
        if (current && Array.isArray(data?.payments)) setPayments(Object.fromEntries(data.payments.map(payment => [payment.profileId, payment])))
      })
      .catch(() => {})
    return () => { current = false }
  }, [key])
  return payments
}
